import { Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCompanyId } from "@/common/services/company.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { IInvoice } from "@/types/invoice.type";
import { getInvoices } from "@/common/services/invoice.api";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import { numberToCurrency } from "@/common/helpers/currency.helpers";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";

// Definição dos cabeçalhos da tabela
const headers = {
  nome_emitente: "Emitente",
  data_emissao: "Data de Emissão",
  situacao: "Situação",
  valor_total: "Valor Total",
};

const STATUS_LABELS = {
  autorizada: "Autorizada",
  cancelada: "Cancelada",
  denegada: "Denegada",
};

type StateTable = {
  itemsPerPage: number;
  totalItems: number;
};
type RenderFunctions = {
  [key: string]: (
    value: unknown,
    item: unknown
  ) => JSX.Element | string | number;
};
export function IncomingInvoicesTable() {
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);

  const [paginationData, setPaginationData] = useState<StateTable>({
    itemsPerPage: 10,
    totalItems: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    status: [] as string[],
    orderBy: "-data_emissao",
  });

  useEffect(() => {
    const loadInvoices = async () => {
      if (!companyId) return;
      setIsLoading(true);
      setInvoices([]);

      const result = await getInvoices({ ...filters, page });

      if (!result.ok || !result.data) {
        notifyError(
          (result as { message?: string })?.message ||
            "Erro desconhecido ao buscar notas fiscais"
        );
        setIsLoading(false);
        return;
      }

      setInvoices(result.data);
      setPaginationData((prev) => ({
        ...prev,
        totalItems: result.totalItems,
      }));
      setIsLoading(false);
    };

    loadInvoices();
  }, [filters, page, companyId, notifyError]);

  const actions = [
    {
      label: "Visualizar",
      action: (data: unknown) => {
        const invoice = data as IInvoice;
        navigate(`/compras/NFSEntry/${invoice._id}`);
      },
    },
  ];

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const handleSort = (orderBy: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      orderBy,
    }));
  };
  const renderers: RenderFunctions = {
    valor_total: (value: unknown) =>
      numberToCurrency(parseFloat((value as string) || "0")),
    data_emissao: (value: unknown) =>
      new Date(value as string).toLocaleDateString("pt-BR"),
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Notas Fiscais de Entrada"
          placeholder="Buscar por emitente, chave..."
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          entity="invoices"
          tableData={invoices as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={invoices as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={({ orderBy }) => handleSort(orderBy)}
          renders={renderers}
          entity="employees"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page}
          totalItems={paginationData.totalItems}
          itemsPerPage={paginationData.itemsPerPage}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
